import { motion } from "framer-motion";
import { useEffect, useContext } from "react";
import { LanguageContext } from "../../../context/LanguageContext";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 1) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.6, ease: "easeOut" },
  }),
};

export default function About() {
  const { t, isRTL } = useContext(LanguageContext);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const offers = t("about.offer.items", { returnObjects: true });
  const whyPoints = t("about.whyChoose.points", { returnObjects: true });

  return (
    <section dir={isRTL ? "rtl" : "ltr"} className="relative bg-white py-24">
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-rose-50/60 via-white to-white" />

      <div className="mx-auto max-w-6xl px-6">

        {/* Header */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mb-20 text-center"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900">
            {t("about.header.title")}
          </h1>
          <p
            className="mt-6 max-w-3xl mx-auto text-lg text-gray-600"
            dangerouslySetInnerHTML={{ __html: t("about.header.subtitle") }}
          />
        </motion.div>

        {/* Intro */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mx-auto max-w-4xl text-center"
        >
          <p className="text-lg text-gray-700 leading-relaxed">
            {t("about.intro")}
          </p>
        </motion.div>

        {/* Mission */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mt-28 grid gap-10 md:grid-cols-2 items-center"
        >
          <div>
            <h2 className="text-3xl font-semibold mb-4 text-gray-900">
              {t("about.mission.title")}
            </h2>
            <p className="text-gray-700 leading-relaxed">
              {t("about.mission.text")}
            </p>
          </div>
        </motion.div>

        {/* What We Offer */}
        <div className="mt-32">
          <motion.h2
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="text-center text-3xl font-semibold mb-14 text-gray-900"
          >
            {t("about.offer.title")}
          </motion.h2>

          <div className="grid gap-8 md:grid-cols-3">
            {offers.map((item, i) => (
              <motion.div
                key={i}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="rounded-2xl border bg-white p-8 shadow-sm"
              >
                <h3 className="text-xl font-semibold mb-3 text-gray-900">
                  {item.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {item.text}
                </p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Why Choose */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mt-32 text-center"
        >
          <h2 className="text-3xl font-semibold mb-12 text-gray-900">
            {t("about.whyChoose.title")}
          </h2>

          <div className="mx-auto grid max-w-4xl gap-4 sm:grid-cols-2">
            {whyPoints.map((point, i) => (
              <div
                key={i}
                className="rounded-xl border p-5 text-gray-700"
              >
                {point}
              </div>
            ))}
          </div>
        </motion.div>

        {/* Commitment */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mt-32 mx-auto max-w-4xl text-center"
        >
          <h2 className="text-3xl font-semibold mb-4 text-gray-900">
            {t("about.commitment.title")}
          </h2>
          <p className="text-gray-700 leading-relaxed">
            {t("about.commitment.text")}
          </p>
        </motion.div>

        {/* Contact */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mt-32 rounded-3xl bg-gradient-to-r from-gray-900 to-gray-800 p-10 text-center text-white"
        >
          <h2 className="text-3xl font-semibold mb-4">
            {t("about.contact.title")}
          </h2>
          <p className="mb-4 text-gray-300">
            {t("about.contact.subtitle")}
          </p>
          <p className="font-medium">
            {t("about.contact.email")} ·{" "}
            <a
              href={t("about.contact.website")}
              target="_blank"
              rel="noreferrer"
              className="underline underline-offset-4"
            >
              {t("about.contact.website")}
            </a>
          </p>
        </motion.div>

      </div>
    </section>
  );
}
  